import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  BarChart,
  Bar,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ReferenceLine,
  ResponsiveContainer,
} from 'recharts';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import type { Send } from '../../types/send';
import { ALL_STYLES, CHART_TOOLTIP_STYLE } from '../../utils/stats-utils';

interface ClimbStyleEffortChartProps {
  sends: Send[];
}

export default function ClimbStyleEffortChart({ sends }: ClimbStyleEffortChartProps) {
  const { t } = useTranslation();

  const data = useMemo(() => {
    return ALL_STYLES.flatMap(style => {
      const styleSends = sends.filter(
        s => s.attributes.climb_style?.includes(style) && s.attributes.grade_opinion != null,
      );
      if (styleSends.length === 0) return [];
      const avg = styleSends.reduce((sum, s) => sum + (s.attributes.grade_opinion ?? 0.5), 0) / styleSends.length;
      return [{
        label: t(`climbTypes.${style}`),
        value: parseFloat((0.5 - avg).toFixed(3)),
      }];
    });
  }, [sends, t]);

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-base">{t('stats.climbStyleEffort.title')}</CardTitle>
      </CardHeader>
      {data.length === 0 ? (
        <CardContent className="py-8 text-center text-sm text-muted-foreground">
          {t('stats.insufficientData')}
        </CardContent>
      ) : (
        <CardContent className="overflow-x-auto px-2 pb-4">
          <p className="text-xs text-muted-foreground mb-3">{t('stats.climbStyleEffort.description')}</p>
          <div style={{ minWidth: 320 }}>
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={data} margin={{ top: 4, right: 8, left: -16, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                <YAxis
                  domain={[-0.4, 0.4]}
                  ticks={[-0.4, -0.2, 0, 0.2, 0.4]}
                  tick={{ fontSize: 11 }}
                  tickFormatter={(v: number) => v.toFixed(1)}
                />
                <Tooltip
                  {...CHART_TOOLTIP_STYLE}
                  formatter={(value) => [Number(value).toFixed(2), t('stats.climbStyleEffort.effortIndex')]}
                />
                <ReferenceLine y={0} stroke="hsl(var(--border))" strokeWidth={1.5} />
                <Bar dataKey="value">
                  {data.map((entry, index) => (
                    <Cell
                      key={`cell-${index}`}
                      fill={entry.value >= 0 ? '#22c55e' : '#ef4444'}
                    />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      )}
    </Card>
  );
}
